import { defineStore } from 'pinia'
import { ref } from 'vue'
import type { RouteLocationNormalized } from 'vue-router'

export interface ProjectNavEntry {
  title: string
  fullPath: string
  ts: number
  name?: string
}

const STORAGE_KEY = 'rv_project_nav_history_v1'
const MAX_PER_PROJECT = 10
const MAX_PROJECTS = 20

function safeJsonParse(value: string): any | null {
  try {
    return JSON.parse(value)
  } catch {
    return null
  }
}

function toRouteTitle(to: RouteLocationNormalized): string {
  const name = String(to.name || '')
  const p = to.params as any

  // Common project routes
  switch (name) {
    case 'project-dashboard': return 'Dashboard'
    case 'project-sessions': return 'Sessions'
    case 'project-live-sessions': return 'Live sessions'
    case 'project-categories': return 'Session categories'
    case 'project-category-view': return p?.categoryId ? `Category ${String(p.categoryId)}` : 'Category'
    case 'project-session-view': return p?.sessionId ? `Session ${String(p.sessionId)}` : 'Session'
    case 'project-session-live-view': return p?.sessionId ? `Live session ${String(p.sessionId)}` : 'Live session'
    case 'project-visitors': return 'Visitors'
    case 'project-visitor-view': return p?.visitorId ? `Visitor ${String(p.visitorId)}` : 'Visitor'
    case 'project-events': return 'Events'
    case 'project-event-analytics': return p?.eventId ? `Event analytics ${String(p.eventId)}` : 'Event analytics'
    case 'project-heatmaps': return 'Heatmaps'
    case 'project-heatmap-view': return p?.heatmapId ? `Heatmap ${String(p.heatmapId)}` : 'Heatmap'
    case 'project-members': return 'Members'
    case 'project-settings-general': return 'Settings / General'
    case 'project-settings-masking': return 'Settings / Masking'
    case 'project-settings-ip-exclusion': return 'Settings / IP exclusion'
    case 'project-settings-tracking': return 'Settings / Tracking'
    case 'project-settings-ai-insights': return 'Settings / AI insights'
    case 'project-settings-ai-usage': return 'Settings / AI usage'
    case 'project-settings-billing': return 'Settings / Billing'
    case 'project-settings-emails': return 'Settings / Emails'
    case 'project-settings-integrations': return 'Settings / Integrations'
  }

  // Fallback: use a readable slice of the path
  try {
    const path = String(to.path || '')
    const pieces = path.split('/').filter(Boolean)
    // `/projects/:id/...` => show the tail
    const tail = pieces.slice(2).join(' / ')
    return tail ? tail : 'Project'
  } catch {
    return 'Project'
  }
}

export const useNavigationHistoryStore = defineStore('navigationHistory', () => {
  const byProjectId = ref<Record<string, ProjectNavEntry[]>>({})
  const hydrated = ref(false)

  function hydrate() {
    if (hydrated.value) return
    hydrated.value = true
    if (typeof window === 'undefined') return

    try {
      const raw = window.localStorage.getItem(STORAGE_KEY)
      if (!raw) return
      const parsed = safeJsonParse(raw)
      if (!parsed || typeof parsed !== 'object') return

      const next: Record<string, ProjectNavEntry[]> = {}
      for (const [projectId, entries] of Object.entries(parsed)) {
        if (typeof projectId !== 'string') continue
        if (!Array.isArray(entries)) continue
        const normalized = entries
          .filter((e: any) => e && typeof e === 'object')
          .map((e: any) => ({
            title: typeof e.title === 'string' ? e.title : 'Project',
            fullPath: typeof e.fullPath === 'string' ? e.fullPath : '',
            ts: typeof e.ts === 'number' ? e.ts : 0,
            name: typeof e.name === 'string' ? e.name : undefined,
          }))
          .filter((e: ProjectNavEntry) => !!e.fullPath)
          .sort((a, b) => b.ts - a.ts)
          .slice(0, MAX_PER_PROJECT)

        if (normalized.length) next[projectId] = normalized
      }

      byProjectId.value = next
    } catch {
      // ignore
    }
  }

  function persist() {
    if (typeof window === 'undefined') return
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(byProjectId.value))
    } catch {
      // ignore quota / private mode errors
    }
  }

  function getRecent(projectId: string): ProjectNavEntry[] {
    hydrate()
    return byProjectId.value[projectId] || []
  }

  function recordProjectNavigation(projectId: string, to: RouteLocationNormalized) {
    if (!projectId) return
    if (!to?.fullPath) return
    hydrate()

    const entry: ProjectNavEntry = {
      title: toRouteTitle(to),
      fullPath: String(to.fullPath),
      ts: Date.now(),
      name: to.name ? String(to.name) : undefined,
    }

    const existing = byProjectId.value[projectId] || []
    const deduped = existing.filter((e) => e.fullPath !== entry.fullPath)
    deduped.unshift(entry)
    byProjectId.value[projectId] = deduped.slice(0, MAX_PER_PROJECT)

    // Keep only the most recently used projects
    const projectIds = Object.keys(byProjectId.value)
    if (projectIds.length > MAX_PROJECTS) {
      const sorted = projectIds
        .map((pid) => ({ pid, ts: (byProjectId.value[pid] || [])[0]?.ts || 0 }))
        .sort((a, b) => b.ts - a.ts)
        .slice(0, MAX_PROJECTS)
      const keep = new Set(sorted.map((x) => x.pid))
      for (const pid of projectIds) {
        if (!keep.has(pid)) delete byProjectId.value[pid]
      }
    }

    persist()
  }

  function clearProject(projectId: string) {
    hydrate()
    if (byProjectId.value[projectId]) {
      delete byProjectId.value[projectId]
      persist()
    }
  }

  return {
    byProjectId,
    getRecent,
    recordProjectNavigation,
    clearProject,
  }
})

